/**
 * Helper functions to load environment specific configuration
 */

import { debug } from '../console-log'
import { CustomLogData } from '../interfaces'

export enum Environment {
  None,
  Test,
  Dev,
  Edog,
  Int,
  Prod,
}

export interface IConfiguration {
  Environment: Environment
  CustomMetadata?: CustomLogData
  BuildTimeStamp?: string
  CustomSessionId?: string
}

export const getEnvironment = (envStr?: string) => {
  const currentEnv = envStr || process.env.NODE_ENV

  switch (currentEnv) {
    case 'development': {
      return Environment.Dev
    }
    case 'test': {
      return Environment.Test
    }
    default: {
      return Environment.Prod
    }
  }
}

// gets the deployed environment based on given url
// eg: http://contentstorage.osi.office.net/dynamiccanvas/WebSurfaces/prod/index.html
export const getDeploymentEnvironment = (
  inputUrl?: string,
  envStr?: string
) => {
  const currentEnv = envStr || process.env.NODE_ENV
  debug(currentEnv, 'Invalid environment!')

  const url = inputUrl || ''
  debug(url, 'Invalid deployment url!')

  const currentUrl = url.toLowerCase()

  // if the url starts with http or https
  const isStorageLocation = currentUrl.indexOf('http') === 0

  let environment: Environment = Environment.None
  if (isStorageLocation) {
    if (
      currentUrl.indexOf('//localhost:') >= 0 ||
      currentUrl.indexOf('//127.0.0.1:') >= 0
    ) {
      environment = Environment.Dev
    } else if (currentUrl.indexOf('/int/') >= 0) {
      environment = Environment.Int
    } else if (currentUrl.indexOf('/edog/') >= 0) {
      environment = Environment.Edog
    } else {
      // default to production for "/prod/"
      environment = Environment.Prod
    }
  } else if (currentEnv === 'development') {
    environment = Environment.Dev
  } else if (currentEnv === 'test') {
    environment = Environment.Test
  } else {
    environment = Environment.Prod
  }

  return environment
}

export const getBuildTimeStamp = () => process.env.BUILD_TIMESTAMP

// gets environment specific information
export class EnvironmentSettings {
  private config: IConfiguration | null = null

  // sets the configuration
  public setConfiguration = (newConfig: IConfiguration) => {
    this.config = newConfig
  }

  public getConfiguration = () => this.config

  public isProduction = () => getEnvironment() === Environment.Prod

  public isTest = () => getEnvironment() === Environment.Test

  public isDevelopment = () => getEnvironment() === Environment.Dev
}

export const currentEnvironment = new EnvironmentSettings()
